import { useState } from "react";
import { Link } from "react-router";
import { ChevronDown, Pencil, Trash2 } from "lucide-react";

import { type DocumentSummary } from "~/api/types";
import { Button } from "~/atoms/button";
import { useElementOverflow } from "~/hooks/useElementOverflow";
import DocumentIcon from "~/molecules/DocumentIcon";
import { cn } from "~/lib/utils";

interface Props {
  pinnedDocument: DocumentSummary;
  canManage: boolean;
  onEdit: () => void;
  onRemove: () => void;
}

export const PinnedDocumentCard = ({
  pinnedDocument,
  canManage,
  onEdit,
  onRemove,
}: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const description = pinnedDocument.pin?.description;
  const { ref: descriptionRef, isOverflowing: isDescriptionTruncated } =
    useElementOverflow<HTMLParagraphElement>();

  const releaseId = (
    pinnedDocument.latestPublishedRelease ?? pinnedDocument.latestRelease
  ).id;

  return (
    <article
      className={cn(
        "border-border bg-background hover:bg-muted/40 h-fit rounded-lg border p-3 transition-colors",
        isExpanded ? "h-fit" : "h-18.5",
      )}
    >
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 items-center gap-2">
          <DocumentIcon
            type={pinnedDocument.latestRelease.artifact.fileType}
            className="size-5"
          />
          <div className="min-w-0 flex-1">
            <Link
              to={`/documents/${pinnedDocument.id}?releaseId=${releaseId}`}
              className="hover:text-primary block truncate text-sm font-medium hover:underline"
              title={pinnedDocument.latestRelease.artifact.title}
            >
              {pinnedDocument.latestRelease.artifact.title}
            </Link>
          </div>
          {canManage && (
            <div className="flex gap-1">
              <Button variant="ghost" size="icon-xs" onClick={onEdit}>
                <Pencil />
              </Button>
              <Button variant="ghost" size="icon-xs" onClick={onRemove}>
                <Trash2 />
              </Button>
            </div>
          )}
        </div>
        <div className="flex gap-1">
          {description && (
            <p
              ref={descriptionRef}
              className={cn(
                "text-muted-foreground text-sm wrap-break-word whitespace-pre-wrap",
                { "line-clamp-1": !isExpanded },
              )}
            >
              {description}
            </p>
          )}
          {(isDescriptionTruncated || isExpanded) && (
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={() => setIsExpanded((current) => !current)}
              className="self-end"
            >
              <ChevronDown
                className={cn("transition-transform duration-200", {
                  "rotate-180": isExpanded,
                })}
              />
            </Button>
          )}
        </div>
      </div>
    </article>
  );
};
